import { z } from 'zod';

// duplicated from the owning domain package (contracts stay domain-free);
// session/application/src/enum-sync.ts asserts they stay in lockstep
export const sessionStatuses = ['SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const;
export const calculatedSessionStatuses = [...sessionStatuses, 'SHOW'] as const;
export const sessionActions = ['REVIEW_NOTE'] as const;

import { metadata } from '@nesso/common-contracts/schema-metadata';
import { treatmentApproachesSchema } from '@nesso/common-contracts/treatment-approaches';

import { practiceLocationIdSchema } from './ids';
import {
    formatSettingsSchema,
    generationStatusSchema,
    nessoNoteContentSchema,
    sessionNoteSourceSchema,
    sessionNoteStatusSchema,
    soapNoteContentSchema,
} from './session-note.models';

export const sessionStatusSchema = z.enum(sessionStatuses);
export type SessionStatus = z.infer<typeof sessionStatusSchema>;

export const calculatedSessionStatusSchema = z.enum(calculatedSessionStatuses);

export const sessionActionsSchema = z.array(z.enum(sessionActions));

export { sessionInputMethods } from './session-note.models';

export const inputMethodSchema = z.enum(['LIVE_RECORDING', 'DICTATION']);

const sessionNoteOutputBaseFields = {
    id: z.uuid(),
    source: sessionNoteSourceSchema,
    status: sessionNoteStatusSchema,
    doneAt: z.date().nullable(),
    deletedAt: z.date().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    generator: z
        .object({
            status: generationStatusSchema,
            settings: formatSettingsSchema,
            feedback: z.boolean().nullable(),
            generatedAt: z.date().nullable(),
            errorType: z.string().nullable(),
        })
        .nullable(),
};

export const sessionNoteOutputSchema = z.discriminatedUnion('type', [
    z.object({
        ...sessionNoteOutputBaseFields,
        type: z.literal('SOAP'),
        content: soapNoteContentSchema.nullable(),
    }),
    z.object({
        ...sessionNoteOutputBaseFields,
        type: z.literal('NESSO'),
        content: nessoNoteContentSchema.nullable(),
    }),
]);

export const sessionSchema = z.object({
    id: z.uuid(),
    date: z.date().transform((x) => x.toISOString()),
    duration: z.number(),
    status: calculatedSessionStatusSchema,
    clients: z.array(
        z.object({
            id: z.uuid(),
            name: z.string(),
        }),
    ),
    treatmentApproaches: treatmentApproachesSchema,
    actions: sessionActionsSchema,
    currentNote: sessionNoteOutputSchema.nullable(),
    inputMethod: inputMethodSchema.nullable(),
    isTranscriptAutoDeleted: z.boolean().nullable(),
    transcriptDeletedAt: z
        .date()
        .transform((x) => x.toISOString())
        .nullable(),
    generatedNoteDeletedAt: z
        .date()
        .transform((x) => x.toISOString())
        .nullable(),
});
export type Session = z.infer<typeof sessionSchema>;

// What a reader supplies, before the schema serializes its dates on the way out.
export type SessionRecord = z.input<typeof sessionSchema>;

export { treatmentApproachesSchema };

export const SESSION_LOCATION_TYPES = ['IN_PERSON', 'TELEHEALTH', 'PHONE'] as const;

export const sessionLocationTypeSchema = z.enum(SESSION_LOCATION_TYPES);

// Copy of practice's `telehealthLinkSchema` (practice-member-settings.models.ts) — session-contracts
// may depend only on common-contracts, so the rule is duplicated rather than imported.
export const sessionTelehealthLinkSchema = z
    .string()
    .trim()
    .min(1, 'Enter a valid link')
    .pipe(z.url({ message: 'Enter a valid link' }))
    .refine(
        (value) => {
            try {
                return new URL(value).protocol === 'https:';
            } catch {
                return false;
            }
        },
        { message: 'Enter a valid link' },
    );

// Copy of practice's `businessPhoneSchema` (practice.models.ts) — session-contracts may depend only
// on common-contracts, so the rule is duplicated rather than imported.
export const sessionPhoneNumberSchema = z
    .string()
    .transform((value) => value.replace(/[\s().+-]/g, ''))
    .transform((digits) =>
        digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits,
    )
    .pipe(z.string().regex(/^\d{10}$/, 'Enter valid 10-digit phone number'));

export const sessionLocationSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('IN_PERSON').meta(metadata.nonSensitive),
        practiceLocationId: practiceLocationIdSchema.nullable().meta(metadata.nonSensitive),
    }),
    z.object({
        type: z.literal('TELEHEALTH').meta(metadata.nonSensitive),
        link: sessionTelehealthLinkSchema.nullable(),
    }),
    z.object({
        type: z.literal('PHONE').meta(metadata.nonSensitive),
        phoneNumber: sessionPhoneNumberSchema.nullable(),
    }),
]);
